import { assertNever } from '../lib/assert-never.js';
import type { ExperienceBlock, ExperiencePage, PageConversation } from '../schemas/experience.js';
import { pageBlocks } from './play.js';

/**
 * The pages PLAYED IN THE CONVERSATION (prolongation.md §3): which they are, which
 * may be, what they show and how a person leaves one.
 *
 * Pure, and shared for the reason `play.ts` gives: the editor greys the box and
 * draws the page in the thread, the reader draws the same items under the
 * bubble, the save refuses on the same run, and Sessions replays the same items.
 * One spelling of « la suite des pages du fil », or the day it changes the
 * editor and the server disagree on which page is in the thread.
 *
 * Functions take the pages of a timeline, or of a draft: all they read is the
 * three fields `ConversationPageLike` names.
 */

/** What these functions read of a page - a stored page and a draft's both fit. */
export type ConversationPageLike = {
  readonly id: string;
  readonly kind: ExperiencePage['kind'];
  readonly conversation: PageConversation | null;
};

/**
 * Whether the page is a page of the thread (§3.1): a CONTENT page whose
 * `conversation` is not null. Says nothing of whether it is in the right place -
 * `conversationRunOf` does, and a ticked page outside the run is the refusal
 * `conversation-page-out-of-run`.
 */
export function isConversationPage(page: ConversationPageLike): boolean {
  return page.kind === 'content' && page.conversation !== null;
}

/**
 * The ids, in order, of the pages of the thread (§3.1): the pages that follow the
 * protocol page DIRECTLY, for as long as they are pages of the thread. The run
 * stops at the first ordinary page. Empty without a protocol page, or when the
 * page after it is ordinary.
 *
 * Structural only: whether the protocol page is empty, whether a page is skipped
 * for a noyau that never came, is the engine's to decide.
 */
export function conversationRunOf(pages: readonly ConversationPageLike[]): string[] {
  const protocolIndex = pages.findIndex((page) => page.kind === 'protocol');
  if (protocolIndex === -1) return [];
  const run: string[] = [];
  for (const page of pages.slice(protocolIndex + 1)) {
    if (!isConversationPage(page)) break;
    run.push(page.id);
  }
  return run;
}

/**
 * Why the box « Jouer dans la conversation » is greyed on a page (§3.1) - the
 * reason the editor words under the box.
 *
 * - `protocol-page`, `feedback-page` - the page is not a content page; the page of
 *   retour never plays in the thread;
 * - `no-protocol-page` - the timeline has no protocol page to follow;
 * - `before-protocol` - the page sits before the protocol page;
 * - `after-ordinary-page` - an ordinary page sits between the protocol page and
 *   this one, so it would not follow the run;
 * - `unknown-page` - defensive: the id is not on the timeline (a stale tab).
 */
export type ConversationIneligibleReason =
  | 'protocol-page'
  | 'feedback-page'
  | 'no-protocol-page'
  | 'before-protocol'
  | 'after-ordinary-page'
  | 'unknown-page';

/** The box is allowed, or greyed for a typed reason. */
export type ConversationEligibility =
  | { readonly allowed: true }
  | { readonly allowed: false; readonly reason: ConversationIneligibleReason };

/**
 * Whether the page MAY be ticked, and why not.
 *
 * Positional and independent of the page's own box: the page is allowed when
 * every page between the protocol page and it is already a page of the thread,
 * so ticking goes from the page right after the protocol page, outwards. A page
 * that is ticked while this answers `after-ordinary-page` is the one the save
 * refuses (`conversation-page-out-of-run`); the box stays ticked and greyed for
 * the operator to untick.
 *
 * The reasons are answered in the order of the type above: what the page IS
 * (protocol, feedback) comes before where it sits.
 */
export function conversationEligibilityOf(
  pages: readonly ConversationPageLike[],
  pageId: string,
): ConversationEligibility {
  const index = pages.findIndex((page) => page.id === pageId);
  const page = pages[index];
  if (page === undefined) return { allowed: false, reason: 'unknown-page' };
  if (page.kind === 'protocol') return { allowed: false, reason: 'protocol-page' };
  if (page.kind === 'feedback') return { allowed: false, reason: 'feedback-page' };
  const protocolIndex = pages.findIndex((candidate) => candidate.kind === 'protocol');
  if (protocolIndex === -1) return { allowed: false, reason: 'no-protocol-page' };
  if (index < protocolIndex) return { allowed: false, reason: 'before-protocol' };
  const between = pages.slice(protocolIndex + 1, index);
  if (!between.every(isConversationPage)) return { allowed: false, reason: 'after-ordinary-page' };
  return { allowed: true };
}

/**
 * The blocks of the thread (§3.3): what a page of the conversation shows under
 * the interviewer's bubble, in the order of the page, its temps flattened (the
 * temps are ignored in the thread).
 *
 * KEPT: a « page suivante » Bouton, a Noyau block in any mode, a Checkbox, a
 * Sélection, a Note, a Lien, a Texte copiable. LEFT OUT: a Texte, an Image, a
 * Son - the bubble replaces them - and what the save refuses in the thread (an
 * Input, a « temps suivant » or « envoyer » Bouton).
 *
 * Display conditions are NOT applied here: they read the session's sources, which
 * the caller holds (`isBlockDisplayed`, §9.2 of the refonte).
 */
export function conversationItemsOf(page: Pick<ExperiencePage, 'temps'>): ExperienceBlock[] {
  return pageBlocks(page).filter(isConversationItem);
}

function isConversationItem(block: ExperienceBlock): boolean {
  switch (block.type) {
    case 'button':
      return block.action === 'next-page';
    case 'noyau':
    case 'checkbox':
    case 'selection':
    case 'note':
    case 'link':
    case 'copyable':
      return true;
    case 'text':
    case 'image':
    case 'sound':
    case 'input':
      return false;
    default:
      return assertNever(block, 'conversation item block type');
  }
}

/**
 * Whether the page has a way out a CLICK can take (§3.4): a Bouton that changes
 * page, or a Noyau block in a choice mode - the portrait is a block to read and
 * no way out. What `conversation-page-no-exit` reads.
 */
export function pageHasConversationExit(page: Pick<ExperiencePage, 'temps'>): boolean {
  return pageBlocks(page).some(
    (block) =>
      (block.type === 'button' && block.action === 'next-page') ||
      (block.type === 'noyau' && block.mode !== 'portrait'),
  );
}
